import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Filter } from 'lucide-react';
import { calendarAPI, propertyAPI } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const OTA_LOGOS = {
  airbnb: '🏠',
  bookingcom: '🔵',
  vrbo: '🌴',
  expedia: '✈️'
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperties.length > 0) {
      fetchCalendarData();
    }
  }, [selectedProperties, currentDate]);

  const fetchProperties = async () => {
    try {
      const res = await propertyAPI.getAll({ limit: 100 });
      const props = res.data.data || [];
      setProperties(props);
      // Select first 3 properties by default
      setSelectedProperties(props.slice(0, 3).map(p => p._id));
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const res = await calendarAPI.getMultiPropertyCalendar({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        propertyIds: selectedProperties.join(',')
      });
      
      setCalendarData(res.data.data);
    } catch (error) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForPropertyAndDate = (propertyId, date) => {
    if (!date) return [];
    
    const propertyData = calendarData.find(p => p.property.id === propertyId);
    if (!propertyData) return [];
    
    return propertyData.events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      const checkDate = new Date(date);
      
      return checkDate >= new Date(eventStart.setHours(0, 0, 0, 0)) && 
             checkDate < new Date(eventEnd.setHours(0, 0, 0, 0));
    });
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const toggleProperty = (propertyId) => {
    if (selectedProperties.includes(propertyId)) {
      setSelectedProperties(selectedProperties.filter(id => id !== propertyId));
    } else {
      setSelectedProperties([...selectedProperties, propertyId]);
    }
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const days = getDaysInMonth();

  if (loading && properties.length === 0) {
    return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">📅 Calendar Overview</h1>
          <p className="page-subtitle">View bookings and availability across all properties</p>
        </div>
      </div>

      {/* Two Column Layout: Property Selector + Calendar */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
        {/* Left Sidebar - Property Selector */}
        <div className="card" style={{ height: 'fit-content', position: 'sticky', top: 20 }}>
          <div className="card-header">
            <span className="card-title">Properties</span>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              {selectedProperties.length} selected
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {properties.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#9CA3AF', fontSize: 13 }}>
                No properties found
              </div>
            ) : (
              <>
                {/* Select All / None */}
                <div style={{ display: 'flex', gap: 8, paddingBottom: 8, borderBottom: '1px solid #E5E7EB' }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => setSelectedProperties(properties.map(p => p._id))}
                  >
                    Select All
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => setSelectedProperties([])}
                  >
                    Clear
                  </button>
                </div>

                {/* Property List */}
                {properties.map(property => {
                  const isSelected = selectedProperties.includes(property._id);
                  return (
                    <div
                      key={property._id}
                      onClick={() => toggleProperty(property._id)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: `2px solid ${isSelected ? '#3B82F6' : '#E5E7EB'}`,
                        background: isSelected ? '#EFF6FF' : '#FFFFFF',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                      }}
                    >
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        border: `2px solid ${isSelected ? '#3B82F6' : '#D1D5DB'}`,
                        background: isSelected ? '#3B82F6' : '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontSize: 13, 
                          fontWeight: 600, 
                          color: '#111827',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {property.title}
                        </div>
                        <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                          ${property.pricing?.basePrice || 0}/night
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {/* Right Side - Calendar */}
        <div>

      {/* Calendar Header */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-ghost btn-icon" onClick={handlePrevMonth}>
              <ChevronLeft size={20} />
            </button>
            <h2 style={{ fontSize: 18, fontWeight: 700, minWidth: 180, textAlign: 'center' }}>
              {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button className="btn btn-ghost btn-icon" onClick={handleNextMonth}>
              <ChevronRight size={20} />
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleToday}>
              Today
            </button>
          </div>
        </div>

        {selectedProperties.length === 0 ? (
          <div className="empty-state">
            <CalendarIcon size={48} />
            <h3>No properties selected</h3>
            <p>Select properties from the filters above to view their calendars</p>
          </div>
        ) : (
          <>
            {/* Day Headers */}
            <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(7, 1fr)`, gap: 4, marginBottom: 8 }}>
              <div style={{ padding: 8 }} />
              {DAYS.map(day => (
                <div key={day} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#6B7280', padding: 8 }}>
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid - One row per property */}
            {calendarData.map(({ property, events }) => (
              <div key={property.id} style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(7, 1fr)`, gap: 4 }}>
                  {/* Property Name Column */}
                  <div style={{ 
                    padding: 8, 
                    display: 'flex', 
                    alignItems: 'center',
                    borderRight: '2px solid #E5E7EB',
                    position: 'sticky',
                    left: 0,
                    background: '#FFFFFF',
                    zIndex: 1
                  }}>
                    <div>
                      <div 
                        style={{ 
                          fontSize: 13, 
                          fontWeight: 600, 
                          color: '#111827',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        onClick={() => navigate(`/properties/${property.id}`)}
                      >
                        {property.title}
                      </div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                        ${property.basePrice}/night
                      </div>
                    </div>
                  </div>

                  {/* Calendar Days */}
                  {days.map((date, index) => {
                    const dayEvents = date ? getEventsForPropertyAndDate(property.id, date) : [];
                    
                    return (
                      <div
                        key={index}
                        style={{
                          minHeight: 60,
                          padding: 4,
                          border: '1px solid #E5E7EB',
                          borderRadius: 4,
                          background: !date ? '#F9FAFB' : isToday(date) ? '#FEF3C7' : '#FFFFFF',
                          position: 'relative'
                        }}
                      >
                        {date && (
                          <>
                            <div style={{ 
                              fontSize: 11, 
                              fontWeight: isToday(date) ? 700 : 400, 
                              color: isToday(date) ? '#D97706' : '#6B7280',
                              marginBottom: 2
                            }}>
                              {date.getDate()}
                            </div>
                            
                            {dayEvents.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {dayEvents.slice(0, 2).map(event => (
                                  <div
                                    key={event.id}
                                    style={{
                                      padding: '3px 5px',
                                      borderRadius: 3,
                                      background: event.color,
                                      color: '#FFFFFF',
                                      fontSize: 9,
                                      fontWeight: 600,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2
                                    }}
                                    onClick={() => navigate(`/bookings/${event.id}`)}
                                    title={`${event.title} - ${event.otaSource || 'Direct'}`}
                                  >
                                    <span>{event.otaSource ? OTA_LOGOS[event.otaSource] : '📅'}</span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {event.otaSource 
                                        ? event.otaSource.replace('bookingcom', 'Booking.com').toUpperCase()
                                        : event.title.split(' ')[0]
                                      }
                                    </span>
                                  </div>
                                ))}
                                {dayEvents.length > 2 && (
                                  <div style={{ fontSize: 8, color: '#6B7280', textAlign: 'center' }}>
                                    +{dayEvents.length - 2}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 20, padding: 16, background: '#F9FAFB', borderRadius: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: '#10B981' }} />
                <span style={{ fontSize: 12, color: '#6B7280' }}>Direct Booking</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: '#FF5A5F' }} />
                <span style={{ fontSize: 12, color: '#6B7280' }}>Airbnb</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: '#003580' }} />
                <span style={{ fontSize: 12, color: '#6B7280' }}>Booking.com</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: '#0D47A1' }} />
                <span style={{ fontSize: 12, color: '#6B7280' }}>VRBO</span>
              </div>
            </div>
          </>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
